import { jsx as _jsx } from "react/jsx-runtime";
import { useDashboardTheme } from "feature/dashboard";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import getCustomLegend from "utils/ChartHelpers.js/CustomLegendPlugin";
import { cn } from "utils/common.utils";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, } from "chart.js";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
/**
 * Custom Hook to generate Chart.js Bar options based on mode and container ID.
 * This hook generates options immutably and memoizes the result for performance.
 *
 * @param {string} containerId - ID of the container element where the custom HTML legend will be placed.
 * @returns {object} The complete Chart.js options object.
 */
export function useBarChart(containerId) {
    // Use useMemo to ensure the options object is only re-created
    // when the dependencies (containerId) change.
    const options = useMemo(() => {
        // --- 1. Define Base Configuration ---
        const baseCommonOptions = {
            maintainAspectRatio: false,
            responsive: true,
            scales: {
                x: {
                    stacked: true,
                },
                y: {
                    stacked: true,
                    ticks: {
                        stepSize: 400000,
                        display: false,
                    },
                },
            },
        };
        const basePlugins = {
            legend: {
                display: false,
            },
            htmlLegend: {
                // ID of the container to put the legend in
                containerID: containerId,
            },
            tooltip: {
                mode: "index",
                interaction: { intersect: false },
            },
        };
        // --- 2. Create Initial Options Object (Full Copy) ---
        let finalOptions = Object.assign(Object.assign({}, baseCommonOptions), { plugins: Object.assign({}, basePlugins) });
        return finalOptions;
    }, [containerId]);
    const { isDefaultStyle, primaryColor, primaryColor_05 } = useDashboardTheme();
    const successColorCode = isDefaultStyle ? "#77D3E1" : primaryColor;
    const failureColorCode = isDefaultStyle ? "#706CF9" : primaryColor_05;
    return {
        BarChart: ({ data }) => {
            const modifiedData = Object.assign(Object.assign({}, data), { datasets: data.datasets.map((ds) => {
                    var _a, _b, _c, _d;
                    return Object.assign(Object.assign({}, ds), { backgroundColor: (_a = ds.backgroundColor) !== null && _a !== void 0 ? _a : successColorCode, borderColor: (_b = ds.borderColor) !== null && _b !== void 0 ? _b : successColorCode, borderWidth: (_c = ds.borderWidth) !== null && _c !== void 0 ? _c : 0, barThickness: (_d = ds.barThickness) !== null && _d !== void 0 ? _d : 20 });
                }) });
            return (_jsx(Bar, { data: modifiedData, options: options, plugins: [getCustomLegend()] }));
        },
        BarChartFooter: ({ className }) => (_jsx("div", { id: containerId, className: cn("mt-8", className) })),
    };
}
